import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setSessionToken, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, Sparkles, Wallet, Gift, Loader2, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("ref_activate") || "";
  const nav = useNavigate();
  const { formatApiError: fmtErr } = useAuth() || {};

  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(null);
  const [shopName, setShopName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Activation link missing. Please use the link from your WhatsApp message.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/activate/verify?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (cancelled) return;
        setPartner(r.data);
        // Pre-fill shop name with partner's first name so it feels natural.
        const first = (r.data.partner_name || "").split(" ")[0] || "";
        setShopName(first ? `${first}'s Shop` : "");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(formatApiError(err));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPw) {
      toast.error("Passwords match nahi kar rahe. Dobara check karein.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password kam az kam 6 characters ka hona chahiye.");
      return;
    }
    if (shopName.trim().length < 2) {
      toast.error("Shop name daalain (kam az kam 2 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/activate", {
        token,
        shop_name: shopName.trim(),
        password,
      });
      if (data.access_token) setSessionToken(data.access_token);
      toast.success("Aap ka Shop taiyar hai. Refer & Earn dashboard khol rahe hain...");
      // Hard redirect so AuthProvider re-runs /me and picks up the new owner.
      window.location.href = "/refer";
    } catch (err) {
      toast.error((fmtErr || formatApiError)(err));
    } finally {
      setSubmitting(false);
    }
  };

  const perks = useMemo(() => ([
    { icon: Gift, label: "Referral code preserved", tone: "emerald" },
    { icon: Wallet, label: "Wallet balance transferred", tone: "amber" },
    { icon: ShieldCheck, label: "Commission history intact", tone: "sky" },
    { icon: Sparkles, label: "7-day free trial", tone: "purple" },
  ]), []);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="activate-page">
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Logo size={40} priority />
            <div className="font-display text-xl font-bold">HisaabPro</div>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">
            Activate Your Shop Account
          </h1>
          <p className="text-emerald-50 mt-2 text-sm sm:text-base">
            Aap ka Referral Partner account ab full Shop me upgrade ho raha hai.
          </p>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          {loading && (
            <div className="flex items-center gap-3 text-slate-600" data-testid="activate-loading">
              <Loader2 className="w-5 h-5 animate-spin" />
              Verifying your activation link...
            </div>
          )}

          {!loading && error && (
            <div className="space-y-4" data-testid="activate-error">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <div className="font-semibold mb-1">Activation link ka masla</div>
                <div>{error}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => nav("/login")}
                  variant="outline"
                  data-testid="activate-error-login-btn"
                >
                  Log in instead
                </Button>
                <Button
                  onClick={() => window.open("https://wa.me/923718067478", "_blank")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="activate-error-support-btn"
                >
                  Contact support
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && partner && (
            <>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="activate-partner-summary">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                      Welcome back
                    </div>
                    <div className="font-display text-xl font-bold text-emerald-900 mt-1">
                      {partner.partner_name}
                    </div>
                    <div className="text-xs text-emerald-800 mt-1">
                      {partner.partner_mobile} · {partner.partner_email}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                      Referral code
                    </div>
                    <div className="font-display text-lg font-bold text-emerald-900 mt-1 font-mono">
                      {partner.referral_code || "—"}
                    </div>
                    <div className="text-xs text-emerald-800 mt-1">
                      Wallet: <b>{fmtPKR(partner.wallet_balance)}</b>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="activate-perks">
                {perks.map((p) => (
                  <div key={p.label} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p.icon className="w-4 h-4 text-slate-600" />
                    <div className="text-xs text-slate-700 mt-1 leading-snug">{p.label}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
                <div className="space-y-2">
                  <Label htmlFor="shop_name" className="flex items-center gap-2">
                    <Store className="w-4 h-4" /> Shop Name
                  </Label>
                  <Input
                    id="shop_name"
                    required
                    minLength={2}
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    placeholder="e.g. Al-Madina Mobiles"
                    data-testid="activate-shop-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      data-testid="activate-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm Password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      required
                      minLength={6}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      placeholder="Retype your password"
                      data-testid="activate-confirm-password"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 flex items-start gap-2" data-testid="activate-notice">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-sky-700" />
                  <div>
                    Aap ka existing <b>Referral Code</b>, <b>Wallet Balance</b> aur <b>Commission
                    History</b> new Shop account me safely transfer ho jayegi. Aap ka email
                    (<b>{partner.partner_email}</b>) hi login ID rahegi.
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="activate-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Activating your shop...
                    </>
                  ) : (
                    "Activate Shop"
                  )}
                </Button>

                <p className="text-[11px] text-slate-500 text-center">
                  Link expires on {new Date(partner.expires_at).toLocaleDateString("en-PK")}. Single use only.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
